import { useDialog } from 'naive-ui'
import { type DialogOptions } from 'naive-ui/es/dialog/src/DialogProvider'
import { h, ref } from 'vue'
import CConfirmationHeader from '@lib/components/CConfirmation/CConfirmationHeader.vue'
import CConfirmationAction from '@lib/components/CConfirmation/CConfirmationAction.vue'
import CConfirmationContent from '@lib/components/CConfirmation/CConfirmationContent.vue'
import { MODAL_WIDTH } from '@lib/constants/modal.const'
import useThemeVars from '@lib/composables/useThemeVars'

interface ConfirmationOptions extends DialogOptions {
  disableCancelWhenLoading?: boolean
  disableDenyWhenLoading?: boolean
  hasCancel?: boolean
  hasExit?: boolean
  cancelText?: string
  exitText?: string
  confirmText?: string
  onConfirm?: () => Promise<boolean | void> | boolean | void
  onExit?: () => Promise<void> | void
  onCancel?: () => Promise<void> | void
  zIndex?: number
}

export default () => {
  const themeVars = useThemeVars()
  const dialog = useDialog()
  const isLoading = ref(false)

  const mergeOptions = (options: ConfirmationOptions, dialogType?: string): DialogOptions => {
    return {
      ...options,
      style: {
        maxWidth: MODAL_WIDTH.Small,
        display: 'flex',
        'flex-direction': 'column',
        gap: '24px',
        padding: '32px',
        background: themeVars.value.base,
        ...(options?.style as Record<string, string> | undefined),
      },

      title: () =>
        h(
          CConfirmationHeader,
          {},
          { default: typeof options.title === 'function' ? options.title : () => options.title },
        ),
      content: () =>
        h(
          CConfirmationContent,
          {},
          { default: typeof options.content === 'function' ? options.content : () => options.content },
        ),
      action: () =>
        h(
          CConfirmationAction,
          {
            disableCancelWhenLoading: options.disableCancelWhenLoading || false,
            disableDenyWhenLoading: options.disableDenyWhenLoading || false,
            hasCancel: options.hasCancel,
            hasExit: options.hasExit,
            confirmText: options.confirmText,
            exitText: options.exitText,
            cancelText: options.cancelText,
            isLoading: isLoading.value,
            isDelete: dialogType === 'danger',
            onCancel: async () => {
              await options?.onCancel?.()
              dialog.destroyAll()
            },
            onExit: async () => {
              await options?.onExit?.()
              dialog.destroyAll()
            },
            onConfirm: async () => {
              isLoading.value = true
              const shouldClose = await options?.onConfirm?.()
              isLoading.value = false

              if (shouldClose !== false) {
                dialog.destroyAll()
              }
            },
          },
          { default: () => options.action },
        ),
      showIcon: false,
      closable: false,
    }
  }

  const create = (options: ConfirmationOptions) => dialog.create(mergeOptions(options, 'create'))
  const success = (options: ConfirmationOptions) => dialog.success(mergeOptions(options, 'success'))
  const warning = (options: ConfirmationOptions) => dialog.warning(mergeOptions(options, 'warning'))
  const error = (options: ConfirmationOptions) => dialog.error(mergeOptions(options, 'error'))
  const danger = (options: ConfirmationOptions) => dialog.error(mergeOptions(options, 'danger'))
  const info = (options: ConfirmationOptions) => dialog.info(mergeOptions(options, 'info'))

  return {
    dialog,
    success,
    create,
    warning,
    error,
    danger,
    info,
  }
}
